import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { isEmpty, isNaN } from 'lodash';
import queryString from 'query-string';
import ITEMS_PER_PAGE_OPTIONS from '_constants/old/_constants/ITEMS_PER_PAGE_OPTIONS';
import qsSearchApplyUtil from 'utils/old/qsSearchApplyUtil';
import { SetQueryParams } from 'utils/old/setQueryParams';
import { isValidDate } from 'utils/old/checkSearchDate';
import sortParam from 'utils/old/sortParam';
import { FilterEntity } from '_entities';

const { applyFilters } = FilterEntity.actions;

/**
 * Shared controller for the new-UI list pages (Retailers, Users, Venues, Youth, ...). Owns the
 * cross-cutting list behaviour every legacy page reimplemented by hand: pagination, sort, search,
 * URL query-param sync, and mount-time restoration from the URL.
 *
 * The consumer stays in control of the entity specifics by supplying:
 *   - `fetchList(params)`: async; performs the entity dispatch. Receives the merged query object
 *     `{ ...filters, ...rest, page, perPage, sortBy, sortOrder }`.
 *   - `defaultSort`: `{ prop, order }` (e.g. DEFAULT_SORT.users).
 *   - `onInit` (optional): runs once on mount (e.g. load a dropdown's options).
 *
 * Returns the state + handlers a list page wires into <ListTable/>, the search field, the filter
 * chips and <TableSizeControls/>.
 */
const useListController = ({ fetchList, defaultSort, onInit }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    oldFilter: { generalFilters },
  } = useSelector((state) => state);

  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [currentPerPage, setCurrentPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[1].value);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState({ sortBy: defaultSort.prop, sortOrder: defaultSort.order });

  // Latest fetchList without making it a reactive dep (avoids re-creating loadList every render).
  const fetchListRef = useRef(fetchList);
  fetchListRef.current = fetchList;

  const loadList = useCallback(
    async ({
      page = currentPage,
      perPage = currentPerPage,
      sortBy = sortOption.sortBy,
      sortOrder = sortOption.sortOrder,
      filters = generalFilters,
      ...rest
    } = {}) => {
      try {
        setLoading(true);
        await fetchListRef.current({ ...filters, ...rest, page, perPage, sortBy, sortOrder });
        SetQueryParams({
          queryString: {
            ...filters,
            ...rest,
            page,
            page_size: perPage,
            ordering: sortParam({ sortBy, sortOrder }),
          },
          history,
        });
      } catch {
        // Reducer marks the failure; the previously-loaded list stays visible.
      } finally {
        setLoading(false);
      }
    },
    [currentPage, currentPerPage, sortOption, generalFilters, history],
  );

  const sortHandler = useCallback(
    (sortBy, sortOrder) => {
      setSortOption({ sortBy, sortOrder });
      loadList({ sortBy, sortOrder });
    },
    [loadList],
  );

  const handlePageChange = useCallback(
    ({ value }) => {
      if (currentPage === value) return;
      setCurrentPage(value);
      loadList({ page: value });
    },
    [loadList, currentPage],
  );

  const handlePerPageChange = useCallback(
    ({ value }) => {
      if (currentPerPage === value) return;
      setCurrentPerPage(value);
      setCurrentPage(1);
      loadList({ perPage: value, page: 1 });
    },
    [loadList, currentPerPage],
  );

  // Quick search — mirrors the legacy handlers (apply on Enter / Search click, reset when cleared).
  const applySearch = (search) => {
    if (search === undefined) {
      qsSearchApplyUtil({ dispatch, getApi: loadList, generalFilters, setCurrentPage });
    } else {
      qsSearchApplyUtil({ search, dispatch, getApi: loadList, generalFilters, setCurrentPage });
    }
  };

  const handleOnChangeInputSearch = (val) => {
    if (isEmpty(val)) {
      setSearchValue('');
      applySearch();
    }
    setSearchValue(val);
  };

  const handleClickInput = () => {
    if (isEmpty(searchValue)) return;
    applySearch(isValidDate(searchValue));
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      if (isEmpty(searchValue)) return;
      applySearch(isValidDate(searchValue));
    }
  };

  const handleRemoveSearch = () => {
    if (isEmpty(searchValue)) return;
    applySearch();
    setSearchValue('');
  };

  // Mount-only init: parse the URL, seed page/perPage/search + filters, fire the initial fetch.
  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;
  const initialSearchRef = useRef(location.search);
  const onInitRef = useRef(onInit);
  onInitRef.current = onInit;

  useEffect(() => {
    const { page, page_size: pageSize, search: searchQuery, ...rest } = queryString.parse(initialSearchRef.current);
    const queryPage = isNaN(+page) || +page === 0 ? 1 : +page;
    const queryPageSize = isNaN(+pageSize) || +pageSize === 0 ? ITEMS_PER_PAGE_OPTIONS[1].value : +pageSize;
    setCurrentPage(queryPage);
    setCurrentPerPage(queryPageSize);
    setSearchValue(searchQuery || '');

    const newFilters = { ...(searchQuery ? { search: searchQuery } : {}), ...rest };
    dispatch(applyFilters({ page, page_size: pageSize, ...newFilters }));
    loadListRef.current({ filters: newFilters, page, perPage: pageSize });

    if (onInitRef.current) onInitRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return {
    loading,
    setLoading,
    searchValue,
    currentPage,
    currentPerPage,
    sortOption,
    setCurrentPage,
    generalFilters,
    loadList,
    sortHandler,
    handlePageChange,
    handlePerPageChange,
    handleOnChangeInputSearch,
    handleClickInput,
    handleKeyDown,
    handleRemoveSearch,
  };
};

export default useListController;
